import { extractCitationIds } from '@/util/agent-citations'
import { AgentReferenceType, ClientAgentReference } from '../store/schemas'
import { AgentReferencesRecord } from '../store/slice'

/**
 * Per-citation info needed to render a turn's footnote markers.
 * `footnoteNumber` is the base label ([1], [2], ...); `occurrences` stores
 * the response-order labels ([1], [1.1], [1.2], ...) when a response message
 * is available.
 */
export type FootnoteInfo = {
  footnoteNumber: number
  highlightCount: number
  occurrences: string[]
  referenceGroupKey: string
}

export type FootnoteInfoByCitationId = Record<string, FootnoteInfo>

type AgentReferenceDisplayCitation = {
  citationId: string
} & Pick<FootnoteInfo, 'footnoteNumber' | 'highlightCount' | 'occurrences'>

type AgentReferenceDisplayHighlight = {
  citationId: string
  citationHighlightIndex: number
  citationLabel: string
}

export type AgentReferenceDisplayGroup = {
  displayCitationId: string
  footnoteNumber: number
  citations: AgentReferenceDisplayCitation[]
  highlights: AgentReferenceDisplayHighlight[]
}

type BuildFootnoteInfoParams = {
  references: ClientAgentReference[]
  responseMessage?: string
}

export function getCitationLabelFromFootnoteInfo(footnoteInfo: FootnoteInfo | undefined, citationIndex: number): string | undefined {
  return footnoteInfo?.occurrences[citationIndex] ?? (footnoteInfo ? String(footnoteInfo.footnoteNumber) : undefined)
}

export function getAgentReferencesForTurn(referencesByCitationId: AgentReferencesRecord, turnId: string | null): ClientAgentReference[] {
  if (!turnId) return []

  return Object.values(referencesByCitationId).filter((reference): reference is ClientAgentReference => {
    if (!reference) return false
    return reference.reference.turn_id === turnId
  })
}

/**
 * Single source of truth for per-turn citation footnote rendering data.
 *
 * Source numbers are assigned once into `sourceNumberByGroupKey`:
 * - With a response message, cited sources are numbered in first body appearance
 *   order and inline occurrence labels are recorded; remaining sources are numbered
 *   in reference registration order after those.
 * - Without a response message, sources are numbered in reference registration order only.
 */
export function buildFootnoteInfoByCitationId(params: BuildFootnoteInfoParams): FootnoteInfoByCitationId {
  const { references, responseMessage } = params
  const footnoteInfoByCitationId: FootnoteInfoByCitationId = {}

  for (const reference of references) {
    footnoteInfoByCitationId[reference.reference.citation_id] = {
      footnoteNumber: 0,
      highlightCount: getReferenceHighlightCount(reference),
      occurrences: [],
      referenceGroupKey: getReferenceDisplayGroupKey(reference),
    }
  }

  const sourceNumberByGroupKey = new Map<string, number>()

  if (responseMessage) {
    const occurrenceCountByGroupKey = new Map<string, number>()

    for (const citationId of extractCitationIds(responseMessage)) {
      const info = footnoteInfoByCitationId[citationId]
      if (!info) continue

      const sourceNumber = assignSourceNumber(sourceNumberByGroupKey, info.referenceGroupKey)
      const occurrenceIndex = (occurrenceCountByGroupKey.get(info.referenceGroupKey) ?? 0) + 1
      occurrenceCountByGroupKey.set(info.referenceGroupKey, occurrenceIndex)

      info.occurrences.push(occurrenceIndex > 1 ? `${sourceNumber}.${occurrenceIndex - 1}` : String(sourceNumber))
    }
  }

  for (const reference of references) {
    const info = footnoteInfoByCitationId[reference.reference.citation_id]
    if (!info) continue

    info.footnoteNumber = assignSourceNumber(sourceNumberByGroupKey, info.referenceGroupKey)
  }

  return footnoteInfoByCitationId
}

export function getCitationIdsOutsideCitedReferenceGroups(
  candidateCitationIds: string[],
  citedCitationIds: string[],
  referencesByCitationId: AgentReferencesRecord
): string[] {
  const citedReferenceGroupKeys = new Set(
    citedCitationIds.flatMap((citationId) => {
      const reference = referencesByCitationId[citationId]
      return reference ? [getReferenceDisplayGroupKey(reference)] : []
    })
  )
  const citedCitationIdSet = new Set(citedCitationIds)

  return candidateCitationIds.filter((citationId) => {
    if (citedCitationIdSet.has(citationId)) return false

    const reference = referencesByCitationId[citationId]
    return reference ? !citedReferenceGroupKeys.has(getReferenceDisplayGroupKey(reference)) : false
  })
}

export function buildAgentReferenceDisplayGroups(
  citationIds: string[],
  footnoteInfoByCitationId: FootnoteInfoByCitationId,
  referencesByCitationId: AgentReferencesRecord
): AgentReferenceDisplayGroup[] {
  const groupsByReferenceKey = new Map<string, AgentReferenceDisplayGroup>()
  const renderedHighlightCountsByCitationId = new Map<string, number>()

  citationIds.forEach((citationId) => {
    const reference = referencesByCitationId[citationId]
    const footnoteInfo = footnoteInfoByCitationId[citationId]
    if (!reference || !footnoteInfo) return

    const groupKey = footnoteInfo.referenceGroupKey
    const group =
      groupsByReferenceKey.get(groupKey) ??
      ({
        displayCitationId: citationId,
        footnoteNumber: footnoteInfo.footnoteNumber,
        citations: [],
        highlights: [],
      } satisfies AgentReferenceDisplayGroup)
    const existingCitation = group.citations.find((citation) => citation.citationId === citationId)
    const citation =
      existingCitation ??
      ({
        citationId,
        footnoteNumber: group.footnoteNumber,
        highlightCount: footnoteInfo.highlightCount,
        occurrences: footnoteInfo.occurrences,
      } satisfies AgentReferenceDisplayCitation)
    const isNewCitation = !existingCitation

    if (isNewCitation) {
      group.citations.push(citation)
    }

    const renderedHighlightCount = renderedHighlightCountsByCitationId.get(citationId) ?? 0
    const occurrence = citation.occurrences[renderedHighlightCount]
    if (occurrence) {
      group.highlights.push({
        citationId,
        citationHighlightIndex: renderedHighlightCount,
        citationLabel: occurrence,
      })
      renderedHighlightCountsByCitationId.set(citationId, renderedHighlightCount + 1)
    } else if (isNewCitation) {
      addRemainingCitationHighlights(group, citation, renderedHighlightCountsByCitationId)
    }

    groupsByReferenceKey.set(groupKey, group)
  })

  groupsByReferenceKey.forEach((group) => {
    group.citations.forEach((citation) => {
      addRemainingCitationHighlights(group, citation, renderedHighlightCountsByCitationId)
    })
  })

  return Array.from(groupsByReferenceKey.values())
}

function getReferenceDisplayGroupKey(reference: ClientAgentReference): string {
  switch (reference.type) {
    case AgentReferenceType.CASELAW:
    case AgentReferenceType.LRR:
      return `${reference.type}:${reference.metadata.s3_dir}`

    case AgentReferenceType.TRELLIS_WRITING_SAMPLE:
      return `${reference.type}:${reference.metadata.display_group_key}`

    case AgentReferenceType.ASSET_EVENT:
      // The same event cited in multiple turns shares one footnote group.
      return `${reference.type}:${reference.metadata.event_id}`

    default:
      return `${reference.type}:${reference.reference.citation_id}`
  }
}

function getReferenceHighlightCount(reference: ClientAgentReference): number {
  return 'highlighted_citations' in reference && Array.isArray(reference.highlighted_citations) ? reference.highlighted_citations.length : 0
}

function assignSourceNumber(sourceNumberByGroupKey: Map<string, number>, groupKey: string): number {
  const existingSourceNumber = sourceNumberByGroupKey.get(groupKey)
  if (existingSourceNumber) return existingSourceNumber

  const sourceNumber = sourceNumberByGroupKey.size + 1
  sourceNumberByGroupKey.set(groupKey, sourceNumber)
  return sourceNumber
}

function addRemainingCitationHighlights(
  group: AgentReferenceDisplayGroup,
  citation: AgentReferenceDisplayCitation,
  renderedHighlightCountsByCitationId: Map<string, number>
): void {
  const renderedHighlightCount = renderedHighlightCountsByCitationId.get(citation.citationId) ?? 0
  const remainingHighlightCount = citation.highlightCount - renderedHighlightCount
  if (remainingHighlightCount <= 0) return

  group.highlights.push(
    ...Array.from({ length: remainingHighlightCount }).map((_, index) => {
      const citationHighlightIndex = renderedHighlightCount + index
      const occurrence = citation.occurrences[citationHighlightIndex]

      return {
        citationId: citation.citationId,
        citationHighlightIndex,
        citationLabel: occurrence ?? String(citation.footnoteNumber),
      }
    })
  )
  renderedHighlightCountsByCitationId.set(citation.citationId, citation.highlightCount)
}
